import type { OpsContainer, OpsTrip } from '@ecobox/firebase';
import type { BadgeTone, ClockState } from '@ecobox/ui';

export function tripLabel(
  trip: Pick<OpsTrip, 'fromBranchId' | 'toBranchId'> & {
    vehiclePlate?: string | undefined;
    vehicleId?: string | undefined;
  },
): string {
  const route = `${trip.fromBranchId} to ${trip.toBranchId}`;
  const vehicle = trip.vehiclePlate || trip.vehicleId;
  return vehicle ? `${route} · ${vehicle}` : route;
}

export function containerLabel(
  c: Pick<OpsContainer, 'containerNo' | 'bookingNo' | 'id' | 'exportHubId' | 'importHubId'>,
): string {
  const name = c.containerNo || c.bookingNo || `…${c.id.slice(-6)}`;
  return `${name} · ${c.exportHubId} to ${c.importHubId}`;
}

const TRIP_STATUS: Record<string, string> = {
  loading: 'Loading',
  departed: 'On the road',
  receiving: 'Being received',
  closed: 'Closed',
};

const CONTAINER_STATUS: Record<string, string> = {
  planned: 'Planned',
  loading: 'Loading',
  closed: 'Sealed',
  departing: 'Leaving',
  departed: 'At sea',
  arrived_port: 'At port',
  pulled_out: 'Out of port',
  receiving: 'Devanning',
  devanned: 'Devanned',
  empty_returned: 'Empty returned',
  completed: 'Completed',
};

export const tripStatusLabel = (s: string): string => TRIP_STATUS[s] ?? s.replace(/_/g, ' ');
export const containerStatusLabel = (s: string): string =>
  CONTAINER_STATUS[s] ?? s.replace(/_/g, ' ');

export function containerStatusTone(s: string): BadgeTone {
  if (s === 'loading' || s === 'receiving') return 'info';
  if (s === 'arrived_port' || s === 'pulled_out') return 'warning';
  if (s === 'completed' || s === 'empty_returned') return 'success';
  return 'neutral';
}

/**
 * Colour for "time in this state". A record's own `nextDueAt` is the moment its earliest clock
 * turns at risk or breaches (13 §13.1); the phone only reads it. No due time, no colour.
 */
export function dueClock(nextDueAt: string | undefined, now: number): ClockState | undefined {
  if (!nextDueAt) return undefined;
  const due = Date.parse(nextDueAt);
  if (Number.isNaN(due)) return undefined;
  if (now >= due) return 'breached';
  return due - now <= 4 * 60 * 60 * 1000 ? 'at_risk' : 'on_track';
}
